import { useCallback } from 'react';
import { useNotify } from 'react-admin';
import { useTranslation } from 'react-i18next';

/**
 * Converts a URL into a File object and invokes a callback when the file is ready.
 *
 * @param allowedExtensionRegExp - Regex to validate accepted file extensions.
 * @param onFileReady - Callback with the converted File.
 * @param setLoadingStatus - Updates loading state (eg: "Uploading... 48%", null when finished)
 */

type UseUrlToFileArgs = {
  allowedRegex: RegExp | undefined;
  onFileReady: (file: File) => void;
  setLoadingStatus?: (status: string | null) => void;
};

export function useUrlToFile({
  allowedRegex,
  onFileReady,
  setLoadingStatus,
}: UseUrlToFileArgs) {
  const { t } = useTranslation();
  const notify = useNotify();

  const urlToFileFn = useCallback(
    async (url: string) => {
      if (!allowedRegex?.test(url)) {
        notify(t('blox.editorial.file.error.invalid'), {
          type: 'warning',
        });
        return;
      }

      try {
        setLoadingStatus?.(t('blox.editorial.file.transfering'));
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';

        xhr.onloadstart = () => {
          setLoadingStatus?.(t('blox.editorial.file.transfering'));
        };

        xhr.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            setLoadingStatus?.(
              `${t('blox.editorial.file.transfering')} ${percentComplete}%`,
            );
          }
        };

        xhr.onload = () => {
          if (xhr.status === 200) {
            const extension = xhr.response.type.split('/')[1] || 'bin';
            const filename = `file_${url.split('/').pop()}.${extension}`;
            const file = new File([xhr.response], filename, { type: xhr.response.type });
            onFileReady(file);
          } else {
            notify(t('blox.editorial.file.error.failed'), { type: 'error' });
          }
          setLoadingStatus?.(null);
        };

        xhr.onerror = () => {
          notify(t('blox.editorial.file.error.failed'), { type: 'error' });
          setLoadingStatus?.(null);
        };

        xhr.open('GET', url);
        xhr.send();
      } catch (error) {
        console.error('[useUrlToFile] Fetch error:', error);
        notify(t('blox.editorial.file.error.failed'), { type: 'error' });
        setLoadingStatus?.(null);
      }
    },
    [t, allowedRegex, notify, onFileReady, setLoadingStatus],
  );

  return urlToFileFn;
}
